import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { submitCompanyDetails, resendVerificationCode } from '../../api/registration';
import Parser from 'html-react-parser';

import { RegistrationContext } from './RegistrationContainer';
import { StoreContext } from '../../components/Store';
import { CONTACT_US_URL } from '../../utils/constants';
import SSORedirectModal from '../Decs/Modals/SSORedirectModal';

const isValidContactName = (name) => {
  return name.trim().length > 3;
};

const CompanyDetails = () => {
  const {
    formData,
    setError,
    handleChange,
    handleBlur,
    countries,
    newErrors,
    isValidEmail,
    isTransferryCustomer,
    setActiveStep,
    setSingleSignOnStatus,
  } = useContext(RegistrationContext);

  const { isSSOUserWithNoCompany } = useContext(StoreContext);
  const isSSOUser = isSSOUserWithNoCompany[0];

  const [docusignLoading, setDocusignLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [showSSORedirectModal, setShowSSORedirectModal] = useState(false);

  useEffect(() => {
    if (isTransferryCustomer && !isSSOUser) {
      setShowSSORedirectModal(true);
    }
  }, [isTransferryCustomer, isSSOUser]);

  const handleResendCode = async () => {
    try {
      const response = await resendVerificationCode(formData.email);
      console.log('Verification code resent:', response);
      setPopupMessage(`Verification Email is sent to ${formData.email}. Please check your junk folder.`);
      setPopupOpen(true);
      setError('');
    } catch (error) {
      console.error('Error resending verification code:', error);
      setError('Failed to resend verification code. Please try again.');
    }
  };
  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  const handleNext = async () => {
    const fieldLabels = {
      ...(isSSOUser ? {} : { verificationCode: 'Verification Code' }),
      name: 'Company Name',
      streetAddress: 'Street Address',
      townOrCity: 'Town / City',
      postcode: 'Postcode',
      countryId: 'Country',
      contactName: 'Contact Name',
      positionInCompany: 'Position in Company',
      contactEmail: 'Contact Email',
      contactPhone: 'Contact Phone Number',
      paymentContactName: 'Payment Contact Name',
      paymentContactEmail: 'Payment Contact Email',
      paymentContactTelephone: 'Payment Contact Telephone',
    };

    const requiredFields = Object.keys(fieldLabels);

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${fieldLabels[field]} is required`;
      }
    });

    // One of either EORI or VAT number is required
    if (!formData['eoriNumber'] && !formData['vatNumber']) {
      newErrors['eoriNumber'] = 'EORI Number is required if you do not have a VAT Number.';
      newErrors['vatNumber'] = 'VAT Number is required if you do not have an EORI Number.';
    }

    // Reset errors
    if (formData.name.length < 4) {
      newErrors['name'] = 'Please enter a valid Company Name.';
    }

    if (!/^\d+$/.test(formData.verificationCode) && !isSSOUser) {
      newErrors['verificationCode'] = 'Verification code does not match';
    }

    if (!isValidContactName(formData.contactName)) {
      newErrors['contactName'] = 'Please enter a valid name.';
    }

    if (!isValidContactName(formData.paymentContactName)) {
      newErrors['paymentContactName'] = 'Please enter a valid name.';
    }

    if (!isValidEmail(formData.paymentContactEmail) && !isTransferryCustomer) {
      newErrors['paymentContactEmail'] = 'Please enter a valid email address.';
    }

    if (formData?.referralCode?.toLowerCase?.() !== '360pay' && formData?.referralCode !== '') {
      newErrors['referralCode'] = 'This referral code is not valid.';
    }

    setFormErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return; // Stop if there are validation errors
    }

    try {
      setDocusignLoading(true);
      const response = await submitCompanyDetails(formData, isSSOUserWithNoCompany[0]);

      if (response.error && !response.success) {
        switch (response.error) {
          case 'Verification code does not match':
          case "Validation failed for parameter 'verificationCode'. Invalid number.":
            setDocusignLoading(false);
            setFormErrors((prevErrors) => {
              const updatedErrors = {
                ...prevErrors,
                verificationCode: 'Verification code does not match',
              };
              return updatedErrors;
            });
            break;
          case 'A record with the same EORI already exists.':
          case 'This EORI already exists in ENS':
            setDocusignLoading(false);

            // if user has entered EORI into vat field, show error against vat field instead (ICS2-477)
            const eoriErrorMessage = `This Company EORI is already registered, please try logging in, or resetting your password. For further assistance please <a href='${CONTACT_US_URL}'>contact us</a>`;
            const field = !formData.eoriNumber || formData.eoriNumber.trim() === '' ? 'vatNumber' : 'eoriNumber';

            setFormErrors((prevErrors) => {
              const updatedErrors = {
                ...prevErrors,
                [field]: eoriErrorMessage,
              };
              return updatedErrors;
            });
            break;
          case 'This Email already exists in ENS':
            setDocusignLoading(false);
            setFormErrors((prevErrors) => {
              const updatedErrors = {
                ...prevErrors,
                contactEmail: `This email address is already registered, please try logging in, or resetting your password. For further assistance please <a href='${CONTACT_US_URL}'>contact us</a>`,
              };
              return updatedErrors;
            });
            break;
          case 'Both EORI and Email already exist in ENS':
            setDocusignLoading(false);
            setFormErrors((prevErrors) => {
              const updatedErrors = {
                ...prevErrors,
                eoriNumber: `This Company EORI is already registered, please try logging in, or resetting your password. For further assistance please <a href='${CONTACT_US_URL}'>contact us</a>`,
                contactEmail: `This email address is already registered, please try logging in, or resetting your password. For further assistance please <a href='${CONTACT_US_URL}'>contact us</a>`,
              };
              return updatedErrors;
            });
            break;
          case 'A record with the same VAT number already exists.':
            setDocusignLoading(false);
            setFormErrors((prevErrors) => {
              const updatedErrors = {
                ...prevErrors,
                vatNumber: `This VAT Number is already registered, please try logging in, or resetting your password. For further assistance please <a href='${CONTACT_US_URL}'>contact us</a>`,
              };
              return updatedErrors;
            });
            break;
          case 'Both EORI and VAT already exist.':
            setDocusignLoading(false);
            setFormErrors((prevErrors) => {
              const updatedErrors = {
                ...prevErrors,
                vatNumber: `This VAT Number is already registered, please try logging in, or resetting your password. For further assistance please <a href='${CONTACT_US_URL}'>contact us</a>`,
                eoriNumber: `This Company EORI is already registered, please try logging in, or resetting your password. For further assistance please <a href='${CONTACT_US_URL}'>contact us</a>`,
              };
              return updatedErrors;
            });
            break;
          case 'A record with the same Company Name already exists.':
            setDocusignLoading(false);
            setFormErrors((prevErrors) => {
              const updatedErrors = {
                ...prevErrors,
                name: 'A record with the same Company Name already exists.',
              };
              return updatedErrors;
            });
            break;
          default:
            setDocusignLoading(false);
            setError('Failed to submit company details. Please try again.');
            console.log('An error occurred', response.error);
        }
      } else {
        setError('');
        if (!response.consent_required && response.redirect_uri) {
          window.location.href = response.redirect_uri;
        }

        if ((response.isDocuSignSkipped && response.isValid) || response.success) {
          if (response.message === 'Company created in ICS2, but failed to sync with ENS') {
            setSingleSignOnStatus('ENS_SYNC_FAILED');
          } else if (response.message === 'Company created in ICS2. ENS synced.') {
            setSingleSignOnStatus('ENS_SYNCED');
          }

          setActiveStep(2);
        }
      }
    } catch (error) {
      console.log('error ', error.message);
      setError('Failed to submit company details. Please try again.');
    }
  };

  return (
    <Box>
      <SSORedirectModal isOpen={showSSORedirectModal} />

      {docusignLoading && (
        <Backdrop sx={() => ({ color: '#fff', zIndex: 999999999 })} open={docusignLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Dialog
        open={popupOpen}
        onClose={handleClosePopup}
        aria-labelledby="verification-popup-title"
        aria-describedby="verification-popup-description"
      >
        <DialogTitle id="verification-popup-title">Verification Email Sent</DialogTitle>
        <DialogContent>
          <Typography id="verification-popup-description">{popupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {!isSSOUser && (
        <>
          <Typography variant="body2" gutterBottom>
            Please enter your verification code, it may be in your junk folder.
          </Typography>
          <TextField
            fullWidth
            label="Verification Code *"
            variant="outlined"
            name="verificationCode"
            value={formData.verificationCode}
            onChange={handleChange}
            onBlur={handleBlur}
            sx={{ my: 2 }}
            error={!!formErrors.verificationCode}
            helperText={formErrors.verificationCode}
            autoComplete="off"
            inputProps={{ autoComplete: 'new-code', form: 'off' }}
          />
          <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
            If you have not received a verification code, please click here to{' '}
            <a href="#" onClick={handleResendCode}>
              resend
            </a>
            .
          </Typography>
        </>
      )}
      <Typography variant="h6" gutterBottom>
        Company Details
      </Typography>
      <TextField
        fullWidth
        label="Company Name *"
        variant="outlined"
        name="name"
        value={formData.name}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.name} // Error state for the field
        helperText={formErrors.name} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-company-name', form: 'off' }}
      />

      <TextField
        fullWidth
        label="EORI Number"
        variant="outlined"
        name="eoriNumber"
        value={formData.eoriNumber}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.eoriNumber} // Error state for the field
        helperText={formErrors.eoriNumber && Parser(formErrors.eoriNumber)} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-eori', form: 'off' }}
      />

      <TextField
        fullWidth
        label="VAT Number"
        variant="outlined"
        name="vatNumber"
        value={formData.vatNumber}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.vatNumber} // Error state for the field
        helperText={formErrors.vatNumber && Parser(formErrors.vatNumber)} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-vat-number', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Company Number"
        variant="outlined"
        name="companyNumber"
        value={formData.companyNumber}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.companyNumber} // Error state for the field
        helperText={formErrors.companyNumber} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-company-number', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Street Address *"
        variant="outlined"
        name="streetAddress"
        value={formData.streetAddress}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.streetAddress} // Error state for the field
        helperText={formErrors.streetAddress} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-address', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Town / City *"
        variant="outlined"
        name="townOrCity"
        value={formData.townOrCity}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.townOrCity} // Error state for the field
        helperText={formErrors.townOrCity} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-townOrCity', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Postcode *"
        variant="outlined"
        name="postcode"
        value={formData.postcode}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.postcode} // Error state for the field
        helperText={formErrors.postcode} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-postcode', form: 'off' }}
      />

      <TextField
        select // Makes this a dropdown
        fullWidth
        label="Country *"
        variant="outlined"
        name="countryId"
        value={formData.countryId}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.countryId} // Error state for the field
        helperText={formErrors.countryId} // Error message
      >
        {countries.map((country) => (
          <MenuItem key={country.id} value={country.id}>
            {country.countryName}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        label="Contact Name *"
        variant="outlined"
        name="contactName"
        value={formData.contactName}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.contactName} // Error state for the field
        helperText={formErrors.contactName} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-name', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Position in Company *"
        variant="outlined"
        name="positionInCompany"
        value={formData.positionInCompany}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.positionInCompany} // Error state for the field
        helperText={formErrors.positionInCompany} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-position', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Contact Email *"
        variant="outlined"
        name="contactEmail"
        value={formData.contactEmail}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.contactEmail} // Error state for the field
        helperText={formErrors.contactEmail && Parser(formErrors.contactEmail)} // Error message
        disabled
      />

      <TextField
        fullWidth
        label="Contact Phone Number *"
        variant="outlined"
        name="contactPhone"
        value={formData.contactPhone}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.contactPhone} // Error state for the field
        helperText={formErrors.contactPhone} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-phone', form: 'off' }}
      />

      <Typography variant="body2" gutterBottom sx={{ mt: 3 }}>
        Confirm where you require us to send your invoices and who to contact:
      </Typography>

      <TextField
        fullWidth
        label="Payment Contact Name *"
        variant="outlined"
        name="paymentContactName"
        value={formData.paymentContactName}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.paymentContactName} // Error state for the field
        helperText={formErrors.paymentContactName} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-payment-name', form: 'off' }}
        disabled={isTransferryCustomer}
      />

      <TextField
        fullWidth
        label="Payment Contact Email *"
        variant="outlined"
        name="paymentContactEmail"
        value={formData.paymentContactEmail}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.paymentContactEmail} // Error state for the field
        helperText={formErrors.paymentContactEmail} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-payment-email', form: 'off' }}
        disabled={isTransferryCustomer}
      />

      <TextField
        fullWidth
        label="Payment Contact Telephone *"
        variant="outlined"
        name="paymentContactTelephone"
        value={formData.paymentContactTelephone}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.paymentContactTelephone} // Error state for the field
        helperText={formErrors.paymentContactTelephone} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-payment-telephone', form: 'off' }}
        disabled={isTransferryCustomer}
      />

      <TextField
        fullWidth
        label="Referral Code"
        variant="outlined"
        name="referralCode"
        value={formData.referralCode}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 1 }}
        error={!!formErrors.referralCode} // Error state for the field
        helperText={formErrors.referralCode} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-payment-telephone', form: 'off' }}
        disabled={isTransferryCustomer}
      />

      {!isSSOUser && (
        <Typography variant="body2" gutterBottom sx={{ my: 3 }}>
          The next step will take you to our partner site (DocuSign) in order to sign the account application and terms
          and conditions.
        </Typography>
      )}
      <Button variant="contained" onClick={handleNext} sx={{ mt: 2 }}>
        Continue
      </Button>
    </Box>
  );
};

export default CompanyDetails;
